import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, pluck } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { rejectCandidateEmailTemplatesGet } from '@app/collections/email-templates/email-templates.actions';
import { associationStatusConfigurationsLoad } from '@modules/assigns/store/assigns.actions';
import { DangerAlert } from '@shared/alert';

import { LicenseConfigurationService } from '../license-configuration.service';
import {
  licenseConfigurationGet,
  licenseConfigurationGetFail,
  licenseConfigurationGetSuccess,
  licenseGetName,
  licenseGetNameFail,
  licenseGetNameSuccess,
  licenseLoadExtensions,
  licenseLoadExtensionsFail,
  licenseLoadExtensionsSuccess,
} from './license-configuration.actions';
import { Action } from '@ngrx/store';

@Injectable()
export class LicenseConfigurationEffects {
  get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseConfigurationGet),
      mergeMap(() =>
        this.service.getConfiguration().pipe(
          map((response) => licenseConfigurationGetSuccess({ payload: response })),
          catchError((error) => of(licenseConfigurationGetFail(error))),
        ),
      ),
    ),
  );

  getLicenseName$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseGetName),
      mergeMap(() =>
        this.service.getLicenseName().pipe(
          map((response) => licenseGetNameSuccess({ payload: response })),
          catchError((error) => of(licenseGetNameFail({ payload: error }))),
        ),
      ),
    ),
  );

  loadLicenseExtensions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseLoadExtensions),
      mergeMap(() =>
        this.service.loadLicenseExtensions().pipe(
          map((response) => licenseLoadExtensionsSuccess({ licenseExtensions: response })),
          catchError((error) => of(licenseLoadExtensionsFail({ payload: error }))),
        ),
      ),
    ),
  );

  loadLicenseExtensionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseLoadExtensionsSuccess),
      mergeMap(({ licenseExtensions }) => {
        this.featureExtensionsService.setLicenseFeatureExtensions(licenseExtensions);
        let actions = [];
        if (
          !(licenseExtensions || []).some(
            (extension) => extension === AppFeatureExtensions.ASSOCIATION_CONFIGURATIONS_PER_JOB,
          )
        ) {
          actions = [associationStatusConfigurationsLoad({})];
        }
        if (
          (licenseExtensions || []).some(
            (extension) => extension === AppFeatureExtensions.ASSOCIATION_EMAIL_TEMPLATES,
          )
        ) {
          actions = [...actions, rejectCandidateEmailTemplatesGet()];
        }
        return actions;
      }),
    ),
  );

  loadLicenseExtensionsFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseLoadExtensionsFail),
      map(() => new DangerAlert("Can't load feature extensions")),
    ),
  );

  getLicenseNameFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(licenseGetNameFail),
      map(() => new DangerAlert("Can't get the license name")),
    ),
  );

  constructor(
    private actions$: Actions,
    private service: LicenseConfigurationService,
    private featureExtensionsService: MojoFeatureExtensionsService,
  ) {}
}
